import { TrendingUp, TrendingDown, Wallet, PieChart, DollarSign } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const PortfolioSummary = ({ data }) => {
  const navigate = useNavigate();
  const portfolio = data;
  
  if (!portfolio) return null;
  
  const pnl = portfolio.total_profit_loss || portfolio.total_pnl || 0;
  const pnlPct = portfolio.profit_loss_percentage || portfolio.total_pnl_pct || 0;
  const isProfit = pnl >= 0;
  const totalValue = portfolio.total_value || 0;
  const totalInvested = portfolio.total_invested || 0;
  const assetCount = portfolio.asset_count || portfolio.assets?.length || 0;

  return (
    <div className="space-y-3" data-testid="portfolio-summary">
      {/* Portfolio Value - Primary Card */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-purple-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Portfolio Value</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">
                ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid - 2 columns on mobile */}
      <div className="grid grid-cols-2 gap-3">
        {/* Total Invested */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wallet className="w-5 h-5 text-green-500" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Total Invested</p>
                <p className="text-lg sm:text-xl font-bold text-white truncate">
                  ${totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profit/Loss */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${isProfit ? "bg-green-500/20" : "bg-red-500/20"} rounded-lg flex items-center justify-center flex-shrink-0`}>
                {isProfit ? (
                  <TrendingUp className="w-5 h-5 text-green-500" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-500" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Total P&L</p>
                <p className={`text-lg sm:text-xl font-bold truncate ${isProfit ? "text-green-500" : "text-red-500"}`}>
                  {isProfit ? "+" : "-"}${Math.abs(pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className={`text-xs ${isProfit ? "text-green-500" : "text-red-500"}`}>
                  ({isProfit ? "+" : ""}{pnlPct.toFixed(2)}%)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assets Count with View Portfolio Button */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <PieChart className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Total Assets Tracked</p>
                <p className="text-xl font-bold text-white">{assetCount}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/portfolio')}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              View Portfolio
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
